import { useState, useRef, useEffect, useCallback } from "react";

/**
 * A message that clears itself after a delay.
 *
 * Replaces the bare `setTimeout(() => setX(null), n)` written out nine times
 * across the views with three different durations. Those had no cleanup, so
 * a timer outliving its component fired a state update on an unmounted tree;
 * they also stacked, meaning a second message could be cleared early by the
 * first one's pending timer.
 */
export const useTransientMessage = (durationMs: number = 5000) => {
  const [message, setMessage] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const show = useCallback(
    (next: string | null) => {
      // Replace any in-flight timer, so a new message always gets its full
      // duration rather than inheriting the previous one's remainder.
      clearPending();
      setMessage(next);

      if (next !== null) {
        timeoutRef.current = setTimeout(() => {
          setMessage(null);
          timeoutRef.current = null;
        }, durationMs);
      }
    },
    [clearPending, durationMs]
  );

  const clear = useCallback(() => {
    clearPending();
    setMessage(null);
  }, [clearPending]);

  useEffect(() => clearPending, [clearPending]);

  return [message, show, clear] as const;
};
